/**
 * Vista: Monedas y tipo de cambio (ERP F1, B1 · D4 y D7)
 * D4: el TC del día es el que PUBLICA EL DOF ese día: el FIX de Banxico (SF43718) del día hábil anterior, guardado
 *     en la fecha de publicación. Fin de semana e inhábiles: rige el último publicado.
 * D7: euro de Banxico (SF46410), con la fecha de Banxico (el DOF no lo publica). Captura manual con motivo obligatorio (queda en la auditoría).
 * Patrón de 2 pestañas: «Consultar» (lo vigente hoy, la consulta por fecha y el historial) y «Agregar / editar»
 * (la captura manual; el TC no se edita: una captura equivocada se anula y se captura otra).
 */

import catalogos from '../../services/erpCatalogos.service.js';
import permisos from '../../services/permissions.service.js';
import modal from '../../utils/modal.js';
import toast from '../../utils/toast.js';
import { hoyLocal } from '../../utils/fechas.js';
import { esc, errMsg, badge, val } from './erpUi.js';
import { permisosPantalla, pestanasHtml, Pestanas } from '../../utils/pestanasErp.js';

const fmt = (v, d = 4) => (v === null || v === undefined ? '—' : Number(v).toLocaleString('es-MX', { minimumFractionDigits: d, maximumFractionDigits: 6 }));
const FUENTE = { DOF: ['DOF', 'success'], BANXICO: ['Banxico', 'info'], manual: ['Manual', 'warning'] };

function diaSemana(fecha) {
  const [y, m, d] = String(fecha).slice(0, 10).split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString('es-MX', { weekday: 'short', day: '2-digit', month: 'short', year: 'numeric' });
}

export default {
  data: { resumen: null, filas: [], filtros: { moneda: '', fuente: '', desde: '', hasta: '', anulados: false } },

  async render() {
    const hoy = hoyLocal();
    // La pestaña «Agregar / editar» es la captura manual (permiso crear). «Descargar de Banxico» (permiso editar) es una
    // acción, no un formulario: se queda en «Consultar», junto al TC vigente.
    const pp = permisosPantalla('monedas_tc', { editar: null });
    const formulario = `
        <div class="card erp-form-card" id="tcFormCard">
          <div class="card-header"><h2 class="card-title">Captura manual</h2></div>
          <div class="erp-aviso erp-aviso-warn">Sólo cuando no hay dato de Banxico o hay que corregirlo. En la misma fecha, la captura manual gana sobre lo descargado. Pide motivo y queda en la auditoría.</div>
          <form id="tcForm" autocomplete="off">
            <div class="form-row">
              <div class="form-group"><label>Moneda *</label><select id="tc_moneda" class="form-control"><option value="USD">USD · Dólar</option><option value="EUR">EUR · Euro</option></select></div>
              <div class="form-group"><label>Fecha en que aplica *</label><input type="date" id="tc_fecha" class="form-control" value="${hoy}" required /></div>
              <div class="form-group"><label>Pesos por unidad *</label><input type="number" id="tc_valor" class="form-control" step="0.000001" min="0.000001" required placeholder="Ej: 18.4567" /></div>
            </div>
            <div class="form-group">
              <label>Motivo *</label>
              <input type="text" id="tc_motivo" class="form-control" minlength="10" maxlength="250" required placeholder="Ej: Banxico sin dato hoy; tomado del DOF impreso" />
            </div>
            <div class="erp-acciones-form">
              <button type="button" class="btn btn-secondary" id="btnTcCancelar">Cancelar</button>
              <button type="submit" class="btn btn-primary">Guardar captura</button>
            </div>
          </form>
        </div>`;
    const consultar = `
      <div class="card">
        <div class="card-header">
          <h2 class="card-title">Vigente hoy <span class="text-muted" style="font-size:.9rem;font-weight:400;">${esc(diaSemana(hoy))}</span></h2>
          ${permisos.can('monedas_tc', 'editar') ? '<button class="btn btn-secondary" id="btnDescargar">⬇️ Descargar de Banxico</button>' : ''}
        </div>
        <div id="tcAvisoToken"></div>
        <div class="erp-tc-grid" id="tcVigente"><div class="loading-spinner"></div></div>
        <p class="form-help" style="margin-top:.75rem;">El dólar es el que publica el DOF ese día. Se descarga el FIX de Banxico (serie SF43718) y se guarda
        en la fecha en que lo publica el DOF: el FIX de un día hábil sale en el DOF del siguiente día hábil, así que el FIX de hoy ya queda guardado con la fecha
        de su publicación. En fin de semana y días inhábiles rige el último publicado. El euro es el de Banxico (SF46410), con la fecha de Banxico: el DOF no lo publica.</p>
      </div>

        <div class="card">
          <div class="card-header"><h2 class="card-title">¿Qué TC rige en una fecha?</h2></div>
          <div class="form-row">
            <div class="form-group"><label>Moneda</label><select id="q_moneda" class="form-control"><option value="USD">USD</option><option value="EUR">EUR</option></select></div>
            <div class="form-group"><label>Fecha</label><input type="date" id="q_fecha" class="form-control" value="${hoy}" /></div>
            <div class="form-group" style="display:flex;align-items:flex-end;"><button class="btn btn-secondary" id="btnConsultar">Consultar</button></div>
          </div>
          <div id="q_resultado" class="text-muted">—</div>
        </div>

      <div class="filters-card">
        <div class="filters-grid">
          <div class="form-group"><label>Moneda</label><select id="f_moneda" class="form-control"><option value="">Todas</option><option>USD</option><option>EUR</option></select></div>
          <div class="form-group"><label>Fuente</label><select id="f_fuente" class="form-control"><option value="">Todas</option><option value="DOF">DOF</option><option value="BANXICO">Banxico</option><option value="manual">Manual</option></select></div>
          <div class="form-group"><label>Desde</label><input type="date" id="f_desde" class="form-control" /></div>
          <div class="form-group"><label>Hasta</label><input type="date" id="f_hasta" class="form-control" /></div>
          <div class="form-group" style="display:flex;align-items:flex-end;"><label class="erp-check"><input type="checkbox" id="f_anulados" /> Ver anulados</label></div>
        </div>
      </div>
      <div class="card">
        <div class="card-header"><h2 class="card-title">Historial</h2><span class="text-muted" id="tcTotal"></span></div>
        <div class="table-container">
          <table class="table">
            <thead><tr><th>Fecha</th><th>Moneda</th><th style="text-align:right;">Pesos</th><th>Fuente</th><th class="tbl-hide-mobile">Detalle</th><th style="text-align:right;">Acciones</th></tr></thead>
            <tbody id="tcBody"><tr><td colspan="6" class="text-center"><div class="loading-spinner"></div></td></tr></tbody>
          </table>
        </div>
      </div>
    `;
    return pestanasHtml({ id: 'tc', titulo: 'Monedas y tipo de cambio', ...pp, consultar, formulario });
  },

  async afterRender() {
    if (!permisos.canView('monedas_tc')) {
      document.getElementById('content').innerHTML = '<div style="padding:2rem;text-align:center;"><h2>Acceso denegado</h2></div>';
      return;
    }
    window.currentView = this;
    this.tabs = new Pestanas('tc', { alAgregar: () => this.limpiarCaptura() });
    document.getElementById('tcForm')?.addEventListener('submit', (e) => { e.preventDefault(); this.capturar(); });
    document.getElementById('btnTcCancelar')?.addEventListener('click', () => { this.limpiarCaptura(); this.tabs.volver(); });
    document.getElementById('btnConsultar')?.addEventListener('click', () => this.consultar());
    document.getElementById('btnDescargar')?.addEventListener('click', () => this.descargar());
    const f = this.data.filtros;
    ['moneda', 'fuente', 'desde', 'hasta'].forEach((k) => document.getElementById(`f_${k}`)?.addEventListener('change', (e) => { f[k] = e.target.value; this.cargarHistorial(); }));
    document.getElementById('f_anulados')?.addEventListener('change', (e) => { f.anulados = e.target.checked; this.cargarHistorial(); });
    await Promise.all([this.cargarResumen(), this.cargarHistorial()]);
    this.consultar();
  },

  async cargarResumen() {
    try {
      const res = await catalogos.tiposCambio.resumen();
      const r = res?.data;
      this.data.resumen = r;
      const tarjeta = (titulo, v) => {
        const fila = v?.fila;
        return `
          <div class="stat-card" style="display:block;">
            <div class="erp-tc-sub">${titulo}</div>
            <div class="erp-tc-valor">${v?.valor != null ? '$' + fmt(v.valor) : '<span class="text-muted" style="font-size:1rem;">Sin dato</span>'}</div>
            <div class="erp-tc-sub">${fila ? `${FUENTE[fila.fuente] ? FUENTE[fila.fuente][0] : esc(fila.fuente)} del ${esc(diaSemana(fila.fecha))}${String(fila.fecha).slice(0, 10) !== r.hoy ? ' (último publicado)' : ''}` : 'No hay tipo de cambio cargado todavía'}</div>
          </div>`;
      };
      document.getElementById('tcVigente').innerHTML = tarjeta('USD · Dólar (DOF)', r.usd) + tarjeta('EUR · Euro (Banxico)', r.eur) + `
        <div class="stat-card" style="display:block;">
          <div class="erp-tc-sub">Descarga de Banxico</div>
          <div style="font-weight:600;margin:.3rem 0;">${r.token_configurado ? (r.ultima_descarga ? 'Última: ' + esc(String(r.ultima_descarga).slice(0, 16)) : 'Lista, sin descargas aún') : 'Sin token'}</div>
          <div class="erp-tc-sub">${r.filas_descargadas} día(s) descargados · ${r.capturas_manuales} captura(s) manual(es)</div>
        </div>`;
      document.getElementById('tcAvisoToken').innerHTML = r.token_configurado ? '' : `
        <div class="erp-aviso erp-aviso-warn"><strong>Falta el token de Banxico.</strong> Mientras no se configure, la descarga diaria no corre y el TC se captura a mano con su motivo.
        (Es gratuito: banxico.org.mx → SieAPIRest; va en la variable BANXICO_SIE_TOKEN del servidor.)</div>`;
    } catch (err) {
      document.getElementById('tcVigente').innerHTML = `<div style="color:var(--danger);">${esc(errMsg(err, 'No se pudo leer el TC'))}</div>`;
    }
  },

  async consultar() {
    const moneda = val('q_moneda');
    const fecha = val('q_fecha');
    const out = document.getElementById('q_resultado');
    if (!fecha) { out.textContent = 'Elige una fecha'; return; }
    try {
      const res = await catalogos.tiposCambio.vigente(moneda, fecha);
      const r = res?.data;
      if (r?.valor == null) { out.innerHTML = `No hay tipo de cambio de ${esc(moneda)} con fecha igual o anterior al ${esc(diaSemana(fecha))}.`; return; }
      const f = r.fila;
      const mismo = f && String(f.fecha).slice(0, 10) === fecha;
      out.innerHTML = `<div class="erp-tc-valor" style="font-size:1.4rem;">$${fmt(r.valor)}</div>
        <div>${mismo ? 'Publicado ese mismo día' : `No hubo publicación ese día: rige el del <strong>${esc(diaSemana(f.fecha))}</strong>`}
        · fuente ${f ? esc(FUENTE[f.fuente] ? FUENTE[f.fuente][0] : f.fuente) : '—'}${f && f.motivo ? ` · motivo: ${esc(f.motivo)}` : ''}</div>`;
    } catch (err) {
      out.innerHTML = `<span style="color:var(--danger);">${esc(errMsg(err))}</span>`;
    }
  },

  async cargarHistorial() {
    const body = document.getElementById('tcBody');
    const f = this.data.filtros;
    try {
      const res = await catalogos.tiposCambio.listar({ moneda: f.moneda, fuente: f.fuente, desde: f.desde, hasta: f.hasta, incluir_anulados: f.anulados || undefined, limit: 400 });
      this.data.filas = res?.data?.tipos_cambio || [];
      document.getElementById('tcTotal').textContent = `${this.data.filas.length} registro(s)`;
      if (!this.data.filas.length) { body.innerHTML = '<tr><td colspan="6" class="text-center text-muted" style="padding:2rem;">Todavía no hay tipos de cambio. Captura uno a mano o configura el token de Banxico.</td></tr>'; return; }
      body.innerHTML = this.data.filas.map((t) => {
        const [txt, tipo] = FUENTE[t.fuente] || [t.fuente, 'info'];
        const detalle = t.fuente === 'manual'
          ? `${esc(t.capturado_por_nombre || '')}${t.motivo ? ` · ${esc(t.motivo)}` : ''}`
          : `${t.fuente === 'DOF' ? 'FIX del día hábil anterior · ' : ''}Serie ${esc(t.serie_banxico || '')} · ${esc(String(t.obtenido_at || '').slice(0, 16))}`;
        const futuro = String(t.fecha).slice(0, 10) > hoyLocal();
        return `
          <tr ${t.deleted_at ? 'style="opacity:.55;text-decoration:line-through;"' : ''}>
            <td>${esc(diaSemana(t.fecha))}</td>
            <td><strong>${esc(t.moneda)}</strong></td>
            <td style="text-align:right;font-variant-numeric:tabular-nums;">$${fmt(t.valor)}</td>
            <td>${badge(txt, tipo)}${futuro && !t.deleted_at ? ' ' + badge('Se publica ese día', 'info') : ''}${t.deleted_at ? ' ' + badge('Anulado', 'danger') : ''}</td>
            <td class="tbl-hide-mobile" style="font-size:.8rem;">${detalle}</td>
            <td style="text-align:right;">${t.fuente === 'manual' && !t.deleted_at && permisos.can('monedas_tc', 'eliminar')
              ? `<button class="btn-icon btn-danger" title="Anular captura" onclick="window.currentView.anular(${t.id})">🗑️</button>` : ''}</td>
          </tr>`;
      }).join('');
    } catch (err) {
      body.innerHTML = `<tr><td colspan="6" class="text-center" style="color:var(--danger);padding:2rem;">${esc(errMsg(err, 'Error al cargar'))}</td></tr>`;
    }
  },

  async capturar() {
    const body = { moneda: val('tc_moneda'), fecha: val('tc_fecha'), valor: Number(val('tc_valor')), motivo: val('tc_motivo') };
    if (!body.motivo || body.motivo.length < 10) { toast.warning('Explica el motivo de la captura manual (al menos 10 caracteres)'); return; }
    try {
      await catalogos.tiposCambio.capturar(body);
      toast.success(`TC ${body.moneda} del ${body.fecha} guardado`);
      this.limpiarCaptura();
      this.tabs.volver();
      await Promise.all([this.cargarResumen(), this.cargarHistorial()]);
      this.consultar();
    } catch (err) {
      toast.error(errMsg(err));
    }
  },

  limpiarCaptura() {
    const set = (i, v) => { const el = document.getElementById(i); if (el) el.value = v; };
    set('tc_moneda', 'USD');
    set('tc_fecha', hoyLocal());
    set('tc_valor', '');
    set('tc_motivo', '');
  },

  anular(id) {
    const t = this.data.filas.find((x) => x.id === id);
    modal.confirm('Anular captura manual', `<p>¿Anular el TC manual de <strong>${esc(t.moneda)}</strong> del ${esc(diaSemana(t.fecha))} ($${fmt(t.valor)})?</p><p class="text-muted">Vuelve a regir lo publicado por el DOF o Banxico. Queda en la auditoría.</p>`, async () => {
      try { await catalogos.tiposCambio.anular(id); toast.success('Captura anulada'); await Promise.all([this.cargarResumen(), this.cargarHistorial()]); this.consultar(); } catch (err) { toast.error(errMsg(err)); }
    });
  },

  async descargar() {
    const btn = document.getElementById('btnDescargar');
    btn.disabled = true;
    try {
      const res = await catalogos.tiposCambio.descargar({});
      const r = res?.data;
      toast.success(r.series.map((s) => `${s.moneda}: ${s.nuevos} nuevos, ${s.actualizados} cambiados`).join(' · '));
      await Promise.all([this.cargarResumen(), this.cargarHistorial()]);
    } catch (err) {
      toast.error(errMsg(err, 'No se pudo descargar'));
    } finally {
      btn.disabled = false;
    }
  }
};
